// "Offene Punkte übernehmen" (#826) — der Dialog zum Verschieben der offenen
// Schritte einer persönlichen Aufgabe in eine NEUE Aufgabe; die alte wird dabei
// geschlossen.
//
// Bewusst signal-gesteuert und GENAU EINMAL global gemountet (App.tsx): die
// Schritte-Oberfläche existiert an zwei Orten (RecordStepList in der Listen-
// ansicht und die "Schritte"-Sektion der Detailansicht). Beide öffnen denselben
// Dialog, indem sie `carryOverRequestSig` setzen — statt die Maske zweimal zu
// bauen (das Projekt baut Doppelimplementierungen gerade ab, #793).
//
// Öffnen von irgendwo — mehr als die Id braucht der Aufrufer nicht, Titel und
// Schritte holt der Dialog selbst:
//   carryOverStepsSig.value = taskId;   (aus store/carryOverSteps)
//
// Das Signal liegt bewusst NICHT hier, sondern in einem abhängigkeitsfreien
// Leaf-Modul: sonst zöge jeder Auslöser über diesen Import Modal + preact/compat
// in seinen Modulgraphen (siehe store/carryOverSteps.ts).

import { useEffect, useState } from "preact/hooks";
import type { PersonalTaskStep } from "@juuplan/shared";
import { Modal } from "./Modal";
import {
  carryOverSteps,
  fetchPersonalTaskDetail,
  refreshPersonalTasks,
} from "../store/personalTasks";
import { carryOverStepsSig } from "../store/carryOverSteps";
import { openPersonalTaskSig } from "../store/deepLinks";
import { toastError, toastSuccess } from "../lib/toast";

import { Button } from "./ui/Button";

/** Voreingestellte Uhrzeit, wenn eine Erinnerung nur mit Datum gesetzt wird. */
const DEFAULT_REMIND_TIME = "09:00";

export function CarryOverStepsDialog(): preact.JSX.Element | null {
  const taskId = carryOverStepsSig.value;
  if (taskId === null) return null;
  return <Dialog key={taskId} taskId={taskId} />;
}

function Dialog(props: { taskId: number }): preact.JSX.Element {
  const { taskId } = props;
  const [openSteps, setOpenSteps] = useState<PersonalTaskStep[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sourceTitle, setSourceTitle] = useState("");
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [remindDate, setRemindDate] = useState("");
  const [remindTime, setRemindTime] = useState(DEFAULT_REMIND_TIME);
  const [busy, setBusy] = useState(false);

  const close = (): void => {
    carryOverStepsSig.value = null;
  };

  useEffect(() => {
    let cancelled = false;
    // Ein Aufruf liefert Titel UND Schritte — so genügt dem Aufrufer die Id.
    void fetchPersonalTaskDetail(taskId).then(
      (detail) => {
        if (cancelled) return;
        setSourceTitle(detail.title);
        setTitle(detail.title);
        // Nur die OFFENEN Punkte wandern — erledigte bleiben als Historie am
        // alten Termin. Alle sind vorausgewählt (der Normalfall ist "alles").
        const open = (detail.steps ?? []).filter((s) => !s.is_done);
        setOpenSteps(open);
        setSelected(new Set(open.map((s) => s.id)));
      },
      () => {
        if (!cancelled) {
          setOpenSteps([]);
          toastError("Aufgabe konnte nicht geladen werden");
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  const toggle = (id: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    const trimmed = title.trim();
    if (!trimmed || busy || openSteps === null) return;
    setBusy(true);
    try {
      // Erinnerung: Datum + Uhrzeit werden LOKAL interpretiert (new Date auf
      // "YYYY-MM-DDTHH:mm" ist Ortszeit) und erst dann in ISO umgewandelt —
      // kein UTC-Umweg über die Datumsfelder.
      let remindAt: string | null = null;
      if (remindDate) {
        const local = new Date(`${remindDate}T${remindTime || DEFAULT_REMIND_TIME}`);
        if (!Number.isNaN(local.getTime())) remindAt = local.toISOString();
      }
      const created = await carryOverSteps(taskId, {
        title: trimmed,
        due_date: dueDate || null,
        remind_at: remindAt,
        step_ids: openSteps.map((s) => s.id).filter((id) => selected.has(id)),
      });
      close();
      void refreshPersonalTasks();
      toastSuccess(
        selected.size === 1
          ? "1 offener Punkt übernommen"
          : `${selected.size} offene Punkte übernommen`
      );
      // In-App-Navigation zur neuen Aufgabe läuft über das Modul-Signal, nicht
      // über einen URL-Parameter (siehe store/deepLinks).
      openPersonalTaskSig.value = created.id;
    } catch {
      toastError("Übernehmen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]";
  const labelClass =
    "block text-xs font-medium text-[color:var(--color-on-surface-variant)] mb-1";

  return (
    <Modal
      title="Offene Punkte übernehmen"
      onClose={close}
      size="md"
      footer={
        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={busy}
            class="rounded-md px-3 py-1.5 text-sm text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-60"
          >
            Abbrechen
          </button>
          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={() => void submit()}
            disabled={busy || openSteps === null || title.trim() === ""}
          >
            Übernehmen &amp; abschliessen
          </Button>
        </div>
      }
    >
      <div class="flex flex-col gap-4">
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">
          Die ausgewählten offenen Punkte werden in eine neue Aufgabe verschoben.
          {sourceTitle ? ` „${sourceTitle}" ` : " Die alte Aufgabe "}
          wird dabei abgeschlossen und behält die erledigten Punkte.
        </p>

        <div>
          <label class={labelClass} for="carry-over-title">
            Titel der neuen Aufgabe
          </label>
          <input
            id="carry-over-title"
            type="text"
            value={title}
            autoFocus
            maxLength={500}
            disabled={busy}
            onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
            class={inputClass}
          />
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class={labelClass} for="carry-over-due">
              Fällig am
            </label>
            <input
              id="carry-over-due"
              type="date"
              value={dueDate}
              disabled={busy}
              onInput={(e) => setDueDate((e.target as HTMLInputElement).value)}
              class={inputClass}
            />
          </div>
          <div>
            <label class={labelClass} for="carry-over-remind-date">
              Erinnerung (optional)
            </label>
            <div class="flex gap-2">
              <input
                id="carry-over-remind-date"
                type="date"
                value={remindDate}
                disabled={busy}
                onInput={(e) => setRemindDate((e.target as HTMLInputElement).value)}
                class={inputClass}
                aria-label="Erinnerung: Datum"
              />
              <input
                type="time"
                value={remindTime}
                disabled={busy || remindDate === ""}
                onInput={(e) => setRemindTime((e.target as HTMLInputElement).value)}
                class={`${inputClass} max-w-[7.5rem]`}
                aria-label="Erinnerung: Uhrzeit"
              />
            </div>
          </div>
        </div>

        <div>
          <span class={labelClass}>Offene Punkte</span>
          {openSteps === null ? (
            <p class="text-xs italic text-[color:var(--color-on-surface-variant)]">
              Schritte laden…
            </p>
          ) : openSteps.length === 0 ? (
            <p class="rounded-md border border-dashed border-[color:var(--color-outline-variant)] px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)]">
              Diese Aufgabe hat keine offenen Punkte. Die neue Aufgabe wird trotzdem
              angelegt und diese hier abgeschlossen.
            </p>
          ) : (
            <ul class="flex flex-col rounded-md border border-[color:var(--color-outline-variant)] divide-y divide-[color:var(--color-outline-variant)]/60">
              {openSteps.map((step) => (
                <li key={step.id}>
                  <label class="flex items-center gap-2.5 px-2.5 py-2 text-sm cursor-pointer hover:bg-[color:var(--color-surface-container-low)]">
                    <input
                      type="checkbox"
                      checked={selected.has(step.id)}
                      disabled={busy}
                      onChange={() => toggle(step.id)}
                      class="size-4 shrink-0 accent-[color:var(--color-primary)]"
                    />
                    <span class="min-w-0 flex-1 truncate text-[color:var(--color-on-surface)]">
                      {step.title}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
